import * as Api from '@ViewModels';
import { ActionItemViewModel, EntityViewModel, IEntity, getTypeForEntity } from '@ViewModels';
import { css } from 'aphrodite';
import { format } from 'date-fns';
import { observer } from 'mobx-react';
import * as React from 'react';
import { DefaultEditableActionItemContentCSS } from '../../../../models';
import { getDisplayName } from '../../../../models/NameUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useCompanyAutocomplete } from '../../../../queries/Company/useCompanyAutocomplete';
import { useContactAutocomplete } from '../../../../queries/Contact/useContactAutocomplete';
import { useLoadAllUsers } from '../../../../queries/User/useLoadAllUsers';
import { CompanyViewModel, ContactViewModel } from '../../../../viewmodels/AppViewModels';
import { Checkbox } from '../../../components-v2/Checkbox';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxList } from '../../../components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemDescription,
	ComboboxAvatarItemTitle,
} from '../../../components-v2/Combobox/ComboboxAvatarItem';
import { DateInput } from '../../../components-v2/DateInput';
import { Field, FieldDescription, FieldError, FieldLabel } from '../../../components-v2/Field';
import { Label } from '../../../components-v2/Label';
import { Pill } from '../../../components-v2/Pill';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { PRIORITIES, PRIORITY_LABELS, priorityColor } from '../../../pages/ActionItems/utils/priority';
import { LoadingSpinner } from '../../LoadingSpinner';
import { UserComboboxAvatarItem } from '../../UserCombobox';
import { PrivateVisibilityIcon } from '../../svgs/icons/PrivateVisibilityIcon';
import { PublicVisibilityIcon } from '../../svgs/icons/PublicVisibilityIcon';
import { PriorityFlagIcon } from '../../svgs/icons/PriorityFlagIcon';
import { ResourceVisibility } from '../../VisibilityDropdown';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../richContent/RichContentDocumentEditor';
import { NO_TIME, applyDay, applyTime, timeKey, timeOptionsFor } from './dueTime';
import { styleSheet } from './styles';
import { ActionItemEditorController } from './useActionItemEditor';

const DefaultEditorConfig: IRichContentDocumentEditorConfig = {
	autoresizeToFitContent: true,
	contentRawCss: DefaultEditableActionItemContentCSS,
	minHeight: 80,
	plugins: [],
	toolbar: false,
};

const PRIORITY_ITEMS = PRIORITIES.map(value => ({ value, label: PRIORITY_LABELS[value] }));

const ABOUT_CONTACT_RESULTS = 5;
const ABOUT_COMPANY_RESULTS = 3;

type AboutEntity = Api.IContact | Api.ICompany;

function isCompany(entity: AboutEntity): entity is Api.ICompany {
	return getTypeForEntity(entity as IEntity) === 'company';
}

function aboutEntityName(entity: AboutEntity): string {
	return isCompany(entity) ? entity.companyName || 'Company' : getDisplayName(entity);
}

function initialsOf(name: string): string {
	return name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(part => part[0]?.toUpperCase() ?? '')
		.join('');
}

const VISIBILITY_ITEMS: { value: ResourceVisibility; label: string }[] = [
	{ value: 'all', label: 'All employees' },
	{ value: 'admin', label: 'Admins only' },
];

interface IProps {
	controller: ActionItemEditorController;
	actionItem: ActionItemViewModel;
	autoFocus?: boolean;
}

/**
 * The chrome-agnostic action-item form: rich-text body, due date and time, priority, assignee,
 * visibility, completed state, and the contacts or companies it is about. Driven by
 * {@link useActionItemEditor}; renders no header or footer so any surface can wrap it.
 */
export const ActionItemEditorFields: React.FC<IProps> = observer(({ controller, actionItem, autoFocus }) => {
	const userSession = useUserSession();
	const completedId = React.useId();
	const {
		assignee,
		checked,
		dueDate,
		hasDueTime,
		priority,
		editorState,
		mentionedEntities,
		visibility,
		inputErrorMessage,
		setAssignee,
		setChecked,
		setDueDate,
		setHasDueTime,
		setPriority,
		setMentionedEntities,
		setVisibility,
		onEditorStateChanged,
		clearError,
	} = controller;
	const disabled = actionItem.isSaving || actionItem.isBusy;
	const { users } = useLoadAllUsers();
	const [aboutSearch, setAboutSearch] = React.useState('');
	const aboutQuery = aboutSearch.trim();
	const contactResults = useContactAutocomplete({
		enabled: aboutQuery.length > 0,
		fragment: aboutQuery,
		pageSize: ABOUT_CONTACT_RESULTS,
	});
	const companyResults = useCompanyAutocomplete({
		enabled: aboutQuery.length > 0,
		fragment: aboutQuery,
		pageSize: ABOUT_COMPANY_RESULTS,
	});
	const aboutItems = React.useMemo<AboutEntity[]>(() => {
		if (!aboutQuery) {
			return [];
		}
		const picked = new Set(mentionedEntities.map(entity => entity.id));
		const contacts = (contactResults.data?.pages[0]?.values ?? []).filter(contact => !picked.has(contact.id));
		const companies = (companyResults.data?.pages[0]?.values ?? []).filter(company => !picked.has(company.id));
		return [...contacts, ...companies];
	}, [aboutQuery, contactResults.data, companyResults.data, mentionedEntities]);

	const timeOptions = React.useMemo(() => timeOptionsFor(dueDate, hasDueTime), [dueDate, hasDueTime]);
	const timeValue = dueDate && hasDueTime ? timeKey(dueDate) : NO_TIME;
	const visibilityItems = React.useMemo(
		() =>
			VISIBILITY_ITEMS.some(item => item.value === visibility)
				? VISIBILITY_ITEMS
				: [...VISIBILITY_ITEMS, { value: visibility, label: 'Selected group' }],
		[visibility]
	);

	const onDayChange = (value: string) => {
		const next = applyDay(value, dueDate, hasDueTime);
		if (next === undefined) {
			return;
		}
		setDueDate(next);
		if (!next) {
			setHasDueTime(false);
		}
	};

	const onTimeChange = (key: string | null) => {
		const nextKey = key ?? NO_TIME;
		setDueDate(applyTime(dueDate ?? new Date(), nextKey));
		setHasDueTime(nextKey !== NO_TIME);
	};

	const onEntitySelected = (item: AboutEntity | null) => {
		if (!item?.id || mentionedEntities.some(existing => existing.id === item.id)) {
			setAboutSearch('');
			return;
		}
		const entity = isCompany(item)
			? new CompanyViewModel(userSession, item)
			: new ContactViewModel(userSession, item as Api.IContact);
		setMentionedEntities([...mentionedEntities, entity as EntityViewModel<IEntity>]);
		setAboutSearch('');
	};

	const removeEntity = (id: string) => setMentionedEntities(mentionedEntities.filter(entity => entity.id !== id));

	return (
		<div className={css(styleSheet.fields)}>
			<Field>
				<FieldLabel>What needs doing</FieldLabel>
				<div className={css(styleSheet.editorBox)}>
					{editorState ? (
						<RichContentDocumentEditor
							autoFocus={autoFocus}
							className={css(styleSheet.editorInput)}
							config={DefaultEditorConfig}
							contentPlaceholderText='Call Bob about the renewal…'
							contentState={editorState}
							onContentStateChanged={onEditorStateChanged}
							onFocus={clearError}
						/>
					) : null}
					{actionItem.isBusy ? (
						<div className={css(styleSheet.loading)}>
							<LoadingSpinner type='small' />
						</div>
					) : null}
				</div>
				<FieldError>{inputErrorMessage}</FieldError>
			</Field>

			<div className={css(styleSheet.grid)}>
				<Field styles={[styleSheet.fullRow]}>
					<FieldLabel>Due date</FieldLabel>
					<div className={css(styleSheet.dueRow)}>
						<div className={css(styleSheet.dueDate)}>
							<DateInput
								value={dueDate ? format(dueDate, 'yyyy-MM-dd') : ''}
								onChange={onDayChange}
								disabled={disabled}
							/>
						</div>
						<Select items={timeOptions} value={timeValue} onValueChange={onTimeChange} disabled={disabled}>
							<SelectTrigger aria-label='Due time' styles={styleSheet.timeTrigger}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{timeOptions.map(option => (
									<SelectItem key={option.value || 'none'} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</Field>

				<Field>
					<FieldLabel>Priority</FieldLabel>
					<Select
						items={PRIORITY_ITEMS}
						value={priority}
						onValueChange={value => value && setPriority(value as Api.ActionItemPriority)}
						disabled={disabled}
					>
						<SelectTrigger aria-label='Priority'>
							<span className={css(styleSheet.selectValue)}>
								<span className={css(styleSheet.selectIcon)}>
									<PriorityFlagIcon
										width={14}
										height={14}
										style={{ color: priorityColor(priority) }}
										fill={priority === 'None' ? 'none' : priorityColor(priority)}
									/>
								</span>
								<SelectValue />
							</span>
						</SelectTrigger>
						<SelectContent>
							{PRIORITY_ITEMS.map(option => (
								<SelectItem key={option.value} value={option.value}>
									<span className={css(styleSheet.selectValue)}>
										<span className={css(styleSheet.selectIcon)}>
											<PriorityFlagIcon
												width={14}
												height={14}
												style={{ color: priorityColor(option.value) }}
												fill={option.value === 'None' ? 'none' : priorityColor(option.value)}
											/>
										</span>
										{option.label}
									</span>
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</Field>

				<Field>
					<FieldLabel>Assigned to</FieldLabel>
					<Combobox
						items={users}
						value={assignee}
						onValueChange={value => setAssignee((value as Api.IUser | null) ?? null)}
						itemToStringLabel={user => getDisplayName(user as Api.IUser)}
						isItemEqualToValue={(item, value) => (item as Api.IUser)?.id === (value as Api.IUser)?.id}
					>
						<ComboboxInput aria-label='Assigned to' placeholder='Unassigned' showClear disabled={disabled} />
						<ComboboxContent>
							<ComboboxEmpty>No teammates match</ComboboxEmpty>
							<ComboboxList>{(user: Api.IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</Field>

				<Field>
					<FieldLabel>Visibility</FieldLabel>
					<Select
						items={visibilityItems}
						value={visibility}
						onValueChange={value => value && setVisibility(value as ResourceVisibility)}
						disabled={disabled}
					>
						<SelectTrigger aria-label='Visibility'>
							<span className={css(styleSheet.selectValue)}>
								<span className={css(styleSheet.selectIcon)}>
									{visibility === 'all' ? <PublicVisibilityIcon /> : <PrivateVisibilityIcon />}
								</span>
								<SelectValue />
							</span>
						</SelectTrigger>
						<SelectContent>
							{visibilityItems.map(option => (
								<SelectItem key={option.value} value={option.value}>
									<span className={css(styleSheet.selectValue)}>
										<span className={css(styleSheet.selectIcon)}>
											{option.value === 'all' ? <PublicVisibilityIcon /> : <PrivateVisibilityIcon />}
										</span>
										{option.label}
									</span>
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</Field>
			</div>

			<Label htmlFor={completedId} styles={[styleSheet.completedRow]}>
				<Checkbox
					id={completedId}
					checked={checked}
					disabled={disabled}
					onCheckedChange={value => setChecked(value === true)}
				/>
				Completed
			</Label>

			<Field>
				<FieldLabel htmlFor='action-item-about-search'>About</FieldLabel>
				<FieldDescription>
					Reference contacts or companies so this shows up on their profile. Nobody is notified.
				</FieldDescription>
				<Combobox
					items={aboutItems}
					value={null}
					inputValue={aboutSearch}
					onInputValueChange={setAboutSearch}
					onValueChange={value => onEntitySelected(value as AboutEntity | null)}
					filter={null}
					itemToStringLabel={item => aboutEntityName(item as AboutEntity)}
					isItemEqualToValue={(item, value) => (item as AboutEntity)?.id === (value as AboutEntity)?.id}
				>
					<ComboboxInput
						id='action-item-about-search'
						placeholder='Add a contact or company'
						showTrigger={false}
						disabled={disabled}
					/>
					<ComboboxContent>
						<ComboboxEmpty>{aboutQuery ? 'No contacts or companies match' : 'Type a name to search'}</ComboboxEmpty>
						<ComboboxList>
							{(item: AboutEntity) => (
								<ComboboxAvatarItem key={item.id} value={item}>
									<ComboboxAvatarItemAvatar
										imageUrl={isCompany(item) ? undefined : (item as Api.IContact).profilePic || undefined}
										fallbackText={initialsOf(aboutEntityName(item))}
									/>
									<div>
										<ComboboxAvatarItemTitle>{aboutEntityName(item)}</ComboboxAvatarItemTitle>
										<ComboboxAvatarItemDescription>
											{isCompany(item) ? 'Company' : (item as Api.IContact).primaryEmail?.value}
										</ComboboxAvatarItemDescription>
									</div>
								</ComboboxAvatarItem>
							)}
						</ComboboxList>
					</ComboboxContent>
				</Combobox>
				{mentionedEntities.length > 0 ? (
					<div className={css(styleSheet.pills)}>
						{mentionedEntities.map(entity => (
							<Pill
								key={entity.id}
								variant='tag'
								color={getTypeForEntity(entity.toJs()) === 'company' ? 'gray' : 'blue'}
								label={entity.name}
								value={entity.id}
								onRemove={disabled ? undefined : removeEntity}
							/>
						))}
					</div>
				) : null}
			</Field>
		</div>
	);
});
